import { DownOutlined, IdcardOutlined, LogoutOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { clearAccessToken } from "../authConfig/token";
import { RouteNames } from "../routes";

/** Two initials from a display name, falling back to the first of an email. */
export function initialsOf(name?: string | null, email?: string | null): string {
    const source = (name ?? "").trim();
    if (source.length > 0) {
        const parts = source.split(/\s+/).slice(0, 2);
        return parts.map(part => part[0]!).join("").toUpperCase();
    }
    return (email ?? "?").trim().charAt(0).toUpperCase() || "?";
}

/**
 * Identity and sign-out, reachable from every screen.
 *
 * Signing out used to mean navigating to the Account section first, which is a
 * page's worth of chrome for one button. The section still exists — this is
 * just the door to it that is always in reach.
 */
export const UserMenu = () => {
    const auth = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const name = auth.user?.profile?.name ?? auth.user?.profile?.preferred_username ?? "";
    const email = auth.user?.profile?.email ?? "";
    const initials = initialsOf(name, email);

    const signOut = async () => {
        setOpen(false);
        clearAccessToken();
        await auth.signoutRedirect({
            post_logout_redirect_uri: window.location.origin,
            id_token_hint: auth.user?.id_token
        });
        await auth.removeUser();
    };

    const overlay = (
        <div className="user-menu">
            <div className="user-menu__head">
                <span className="avatar">{initials}</span>
                <span style={{ minWidth: 0 }}>
                    <div className="user-menu__name">{name || t("menu.account")}</div>
                    {email.length > 0 ? <div className="user-menu__sub">{email}</div> : null}
                </span>
            </div>
            <button
                type="button"
                className="user-menu__item"
                onClick={() => {
                    setOpen(false);
                    navigate(`/${RouteNames.account}`);
                }}
            >
                <IdcardOutlined />
                {t("menu.account")}
            </button>
            <button type="button" className="user-menu__item user-menu__item--danger" onClick={signOut}>
                <LogoutOutlined />
                {t("actions.signOut")}
            </button>
        </div>
    );

    return (
        <Dropdown
            overlay={overlay}
            trigger={["click"]}
            placement="bottomRight"
            visible={open}
            onVisibleChange={setOpen}
        >
            <button type="button" className="user-chip" aria-label={t("menu.account")}>
                <span className="avatar">{initials}</span>
                <span className="user-chip__name">{name || t("menu.account")}</span>
                <DownOutlined className="user-chip__caret" />
            </button>
        </Dropdown>
    );
};
